import type { paths } from './generated.js';

export interface ApiErrorEnvelope {
  readonly error: string;
  readonly message?: string;
  readonly details?: unknown;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly requestId: string | undefined,
    public readonly details?: unknown,
    public readonly retryAfterSeconds?: number,
    message?: string
  ) {
    super(message ?? code);
    this.name = 'ApiError';
  }
}

// Bootstrap-injected handlers. main.ts wires these in once the stores and
// router exist; the client does not import them directly so the dependency
// graph stays acyclic.
let on401: (() => void) | null = null;
export function setApiHandlers(handlers: { readonly on401: () => void }): void {
  on401 = handlers.on401;
}

const BASE_URL = (import.meta.env.VITE_WEB_API_BASE_URL ?? '').replace(/\/$/, '');

export async function apiFetch<Path extends keyof paths>(
  path: Path,
  init: RequestInit & {
    params?: Record<string, string>;
    /**
     * Skip the global `on401` redirect for this call. Set it on the routine
     * "am I logged in?" session probe (`fetchMe`), where a 401 is the expected
     * anonymous result and the caller already handles it — otherwise the probe
     * would hijack navigation to /login even on public pages like
     * /set-password/:token.
     */
    suppressAuthRedirect?: boolean;
  } = {}
): Promise<unknown> {
  const { params, suppressAuthRedirect, ...fetchInit } = init;
  let url = BASE_URL ? `${BASE_URL}${path as string}` : (path as string);
  if (params) {
    const search = new URLSearchParams(params);
    url += `?${search.toString()}`;
  }

  const res = await fetch(url, {
    ...fetchInit,
    credentials: 'include',
    headers: {
      // Only advertise a JSON body when one is actually present. A body-less
      // POST that still sends `Content-Type: application/json` is rejected by
      // Fastify's body parser ("Body cannot be empty when content-type is set
      // to 'application/json'").
      ...(fetchInit.body != null ? { 'Content-Type': 'application/json' } : {}),
      ...(fetchInit.headers ?? {}),
    },
  });

  const requestId = res.headers.get('X-Request-ID') ?? undefined;

  if (res.status === 401) {
    if (!suppressAuthRedirect) on401?.();
    throw new ApiError(401, 'Unauthorized', requestId);
  }

  if (res.status === 429) {
    const retryAfterRaw = res.headers.get('Retry-After');
    const retryAfter = retryAfterRaw ? Number.parseInt(retryAfterRaw, 10) : undefined;
    const body = (await res.json().catch(() => ({}))) as ApiErrorEnvelope;
    throw new ApiError(
      429,
      body.error ?? 'RateLimited',
      requestId,
      body.details,
      Number.isFinite(retryAfter) ? retryAfter : undefined,
      body.message
    );
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ApiErrorEnvelope;
    throw new ApiError(
      res.status,
      body.error ?? 'UnknownError',
      requestId,
      body.details,
      undefined,
      body.message
    );
  }

  if (res.status === 204) return undefined;
  return res.json();
}
